<template>
  <SearchHeaderForm
    ref="searchHeader"
    @update:search-term="(v) => (searchTerm = v)"
  />

  <Drawer
    ref="searchResults"
    :max-width="{ md: '100%', lg: '100%', xl: '100%' }"
    :height="drawerHeight"
  >
    <div class="search-results">
      <GridContainer>
        <GridItem class="search-results-wrapper">
          <div class="search-title">
            <h2>{{ t("drawer.title") }}</h2>
            <div class="post-number" v-if="nFound">
              {{ t("drawer.postNumber", { n: nFound }) }}
            </div>
          </div>
          <div class="search-body" :style="{ height: scrollHeight }">
            <Transition name="fade" mode="out-in">
              <div
                class="search-body-wrapper"
                v-if="posts.length && status == 'success'"
              >
                <PostPreview
                  v-for="post in posts"
                  :post="post"
                  :show-author="false"
                  :show-read-time="false"
                />
              </div>
              <div class="search-body-empty" v-else-if="status == 'success'">
                {{ t("drawer.empty", { search: searchTerm }) }}
              </div>
              <LoadingSpinner v-else />
            </Transition>
          </div>
          <div class="search-footer" v-if="searchTerm">
            <CjaButton
              :type="'primary'"
              :href="`/blog/pesquisa?search=${searchTerm}`"
              :alt-anchor="NuxtLink"
            >
              {{ t("drawer.btn") }}
            </CjaButton>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  </Drawer>
</template>

<script lang="ts" setup>
import { NuxtLink } from "#components";
import {
  CjaButton,
  Drawer,
  GridContainer,
  GridItem,
  LoadingSpinner,
} from "cja-phoenix";
import SearchHeaderForm from "./SearchHeaderForm.vue";
import { useSearchPosts } from "~/composables/storyblok/useSearchPosts";

const { t } = useI18n();

const searchResults = ref();
const searchHeader = ref();
const searchTerm = ref();

const drawerHeight = ref();
const scrollHeight = ref();

const setDrawerHeight = () => {
  const drawerHeightNumber =
    window.innerHeight -
    (searchHeader.value.$el.offsetHeight + searchHeader.value.$el.offsetTop);

  drawerHeight.value = `${drawerHeightNumber}px`;

  scrollHeight.value = `${drawerHeightNumber - 178}px`;
};

const { status, posts, nFound } = useSearchPosts(searchTerm);

const disableScroll = () => {
  document.querySelectorAll("html, body").forEach((el) => {
    (el as HTMLElement).style.overflow = "hidden";
  });
};

const enableScroll = () => {
  document.querySelectorAll("html, body").forEach((el) => {
    (el as HTMLElement).style.overflow = "";
  });
};

watch(
  () => searchTerm.value,
  (n) => {
    if (n && !searchResults.value.active) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      searchResults.value.openDrawer();

      disableScroll();
    } else if (!n && searchResults.value.active) {
      searchResults.value.closeDrawer();

      enableScroll();
    }
  }
);

onMounted(() => {
  window.addEventListener("resize", setDrawerHeight);
  setDrawerHeight();
});

onUnmounted(() => {
  window.removeEventListener("resize", setDrawerHeight);
  enableScroll();
});
</script>

<style lang="scss" scoped>
.search-results {
  height: 100%;
  background: $white;
  padding: 24px 0;

  .search-results-wrapper {
    padding-bottom: 84px;

    .search-title {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 16px;
      align-items: center;
      margin-bottom: 16px;

      h2 {
        font-size: 24px;
        line-height: 30px;
        margin: 0;
      }

      .post-number {
        font-size: 14px;
        line-height: 18px;
      }
    }

    .search-body {
      overflow-y: auto;

      .search-body-wrapper {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;

        @media screen and (min-width: $break-md-min) {
          grid-template-columns: repeat(2, 1fr);
        }

        @media screen and (min-width: $break-lg-min) {
          grid-template-columns: repeat(3, 1fr);
        }

        @media screen and (min-width: $break-xl-min) {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      .search-body-empty {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
      }

      .spinner {
        margin: 32px auto 0;
      }
    }

    .search-footer {
      position: absolute;
      width: 100%;
      left: 0;
      bottom: 0;
      padding: 24px;
      display: flex;
      justify-content: center;
      box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
      background: $white;
    }
  }
}

:deep(.drawer-container) {
  transition-duration: 0s;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease-in-out;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<i18n lang="json">
{
  "pt": {
    "title": "Blog",
    "description": "Dicas, artigos e ferramentas para gerir as tuas finanças pessoais de forma inteligente.",
    "input": {
      "search": {
        "placeholder": "Pesquisa um tópico"
      }
    },
    "drawer": {
      "title": "Resultados",
      "postNumber": "{n} artigos",
      "btn": "Ver todos os resultados",
      "empty": "Não existe nenhum artigo para \"{search}\""
    }
  }
}
</i18n>
